import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaUser, FaSearch, FaEdit, FaCheckCircle, FaClock,
  FaEnvelope, FaTimes, FaLightbulb, FaFilter, FaChevronLeft,
  FaChevronRight, FaStickyNote, FaUserSecret,
} from 'react-icons/fa';
import adminService, { LandingPageIdea, FilterCounts, PaginationData } from '../services/adminService';
import LoadingSpinner from './LoadingSpinner';
import { toast } from 'react-toastify';

const StatCard: React.FC<{
  label: string;
  value: number;
  color: string;
  bg: string;
  icon: React.ReactNode;
}> = ({ label, value, color, bg, icon }) => (
  <div className={`${bg} rounded-2xl p-4 flex items-center gap-3`}>
    <div className={`w-10 h-10 rounded-xl bg-white bg-opacity-60 flex items-center justify-center ${color}`}>
      {icon}
    </div>
    <div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs font-medium text-gray-600">{label}</p>
    </div>
  </div>
);

const LandingPageIdeas: React.FC = () => {
  const [ideas, setIdeas] = useState<LandingPageIdea[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasUserFilter, setHasUserFilter] = useState('');
  const [isProcessedFilter, setIsProcessedFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [filterCounts, setFilterCounts] = useState<FilterCounts | null>(null);
  const [selectedIdea, setSelectedIdea] = useState<LandingPageIdea | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchIdeas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, searchTerm, hasUserFilter, isProcessedFilter, sortBy, sortOrder]);

  const fetchIdeas = async () => {
    try {
      setIsLoading(true);
      const params: any = {
        page: currentPage,
        limit: perPage,
        sortBy,
        sortOrder,
        ...(searchTerm && { search: searchTerm }),
        ...(hasUserFilter && { hasUser: hasUserFilter }),
        ...(isProcessedFilter && { isProcessed: isProcessedFilter }),
      };
      const response = await adminService.getLandingPageIdeas(params);
      const data = response.data || response;
      setIdeas(data.ideas || []);
      setPagination(data.pagination || null);
      setFilterCounts(data.filterCounts || null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load ideas');
      setIdeas([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (ideaId: string, isProcessed: boolean) => {
    try {
      setIsUpdating(true);
      await adminService.updateIdeaStatus(ideaId, { isProcessed, notes });
      toast.success('Status updated successfully');
      fetchIdeas();
      setSelectedIdea(null);
      setNotes('');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-5 pb-10">

      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
          <FaLightbulb className="w-5 h-5 text-yellow-500" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Landing Page Ideas</h1>
          <p className="text-sm text-gray-500">View and manage ideas submitted from the landing page</p>
        </div>
      </div>

      {/* Stats */}
      {filterCounts && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total Ideas" value={filterCounts.total || 0}
            color="text-blue-600" bg="bg-blue-50"
            icon={<FaLightbulb className="w-4 h-4" />} />
          <StatCard label="With User" value={filterCounts.withUser || 0}
            color="text-emerald-600" bg="bg-emerald-50"
            icon={<FaUser className="w-4 h-4" />} />
          <StatCard label="Anonymous" value={filterCounts.withoutUser || 0}
            color="text-orange-600" bg="bg-orange-50"
            icon={<FaUserSecret className="w-4 h-4" />} />
          <StatCard label="Unprocessed" value={filterCounts.unprocessed || 0}
            color="text-red-600" bg="bg-red-50"
            icon={<FaClock className="w-4 h-4" />} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search ideas..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50"
            />
          </div>

          <div className="flex gap-3 flex-wrap">
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
              <select
                value={hasUserFilter}
                onChange={(e) => { setHasUserFilter(e.target.value); setCurrentPage(1); }}
                className="pl-8 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 appearance-none cursor-pointer"
              >
                <option value="">All Users</option>
                <option value="true">With User</option>
                <option value="false">Anonymous</option>
              </select>
            </div>

            <div className="relative">
              <FaCheckCircle className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
              <select
                value={isProcessedFilter}
                onChange={(e) => { setIsProcessedFilter(e.target.value); setCurrentPage(1); }}
                className="pl-8 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 appearance-none cursor-pointer"
              >
                <option value="">All Status</option>
                <option value="false">Pending</option>
                <option value="true">Processed</option>
              </select>
            </div>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field); setSortOrder(order); setCurrentPage(1);
              }}
              className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 appearance-none cursor-pointer"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="pageType-asc">Type A-Z</option>
              <option value="pageType-desc">Type Z-A</option>
            </select>
          </div>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner />
        </div>
      ) : ideas.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FaLightbulb className="w-7 h-7 text-gray-400" />
          </div>
          <p className="text-gray-500 font-medium">No ideas found</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          {ideas.map((idea, index) => (
            <motion.div
              key={idea._id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className={`h-1 w-full ${idea.isProcessed ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        idea.pageType === 'founder' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {idea.pageType || 'founder'}
                      </span>
                      {idea.isProcessed ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                          <FaCheckCircle className="w-3 h-3" /> Processed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                          <FaClock className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </div>

                    {/* Idea Text */}
                    <p className="text-gray-800 text-sm leading-relaxed mb-4 font-medium">{idea.idea}</p>

                    {/* Meta Row */}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1.5">
                        {idea.userId ? (
                          <>
                            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                              <FaUser className="w-2.5 h-2.5 text-blue-600" />
                            </div>
                            <span className="font-medium text-gray-700">
                              {idea.userId?.name || 'User'}
                            </span>
                            <span className="text-gray-400">·</span>
                            <FaEnvelope className="w-3 h-3 text-gray-400" />
                            <span>{idea.userId?.email || idea.email || '—'}</span>
                          </>
                        ) : (
                          <>
                            <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                              <FaUserSecret className="w-2.5 h-2.5 text-gray-400" />
                            </div>
                            <span className="text-gray-500">Anonymous</span>
                            {idea.email && (
                              <>
                                <span className="text-gray-400">·</span>
                                <FaEnvelope className="w-3 h-3" />
                                <span>{idea.email}</span>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FaClock className="w-3 h-3 text-gray-400" />
                        <span>{formatDate(idea.createdAt)}</span>
                      </div>
                    </div>

                    {/* Notes */}
                    {idea.notes && (
                      <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5">
                        <FaStickyNote className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-700">{idea.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Action */}
                  <button
                    onClick={() => { setSelectedIdea(idea); setNotes(idea.notes || ''); }}
                    className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                      idea.isProcessed
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-primary text-white hover:bg-primaryDark'
                    }`}
                  >
                    <FaEdit className="w-3.5 h-3.5" />
                    {idea.isProcessed ? 'Update' : 'Mark Processed'}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-3">
          <p className="text-sm text-gray-500">
            Page <span className="font-semibold text-gray-800">{pagination.currentPage}</span> of{' '}
            <span className="font-semibold text-gray-800">{pagination.totalPages}</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={!pagination.hasPrevPage}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              <FaChevronLeft className="w-3 h-3" />
            </button>
            <button
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={!pagination.hasNextPage}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              <FaChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {selectedIdea && (
          <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary bg-opacity-10 flex items-center justify-center">
                    <FaEdit className="w-4 h-4 text-primary" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">Update Idea Status</h3>
                </div>
                <button
                  onClick={() => { setSelectedIdea(null); setNotes(''); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* Idea Preview */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Idea</p>
                  <p className="text-sm text-gray-800 leading-relaxed">{selectedIdea.idea}</p>
                </div>

                {/* Status Buttons */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Status</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleStatusUpdate(selectedIdea._id, true)}
                      disabled={isUpdating}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all ${
                        selectedIdea.isProcessed
                          ? 'bg-emerald-500 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200'
                      }`}
                    >
                      <FaCheckCircle className="w-4 h-4" /> Processed
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedIdea._id, false)}
                      disabled={isUpdating}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all ${
                        !selectedIdea.isProcessed
                          ? 'bg-amber-500 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700 border border-gray-200'
                      }`}
                    >
                      <FaClock className="w-4 h-4" /> Pending
                    </button>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                    <FaStickyNote className="w-3.5 h-3.5 text-amber-500" /> Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 resize-none"
                    rows={3}
                    placeholder="Add notes about this idea..."
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-1">
                  <button
                    onClick={() => { setSelectedIdea(null); setNotes(''); }}
                    disabled={isUpdating}
                    className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleStatusUpdate(selectedIdea._id, selectedIdea.isProcessed)}
                    disabled={isUpdating}
                    className="px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primaryDark disabled:opacity-50 transition-colors"
                  >
                    {isUpdating ? 'Saving...' : 'Save Notes'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPageIdeas;
